import type { BillingCycle, BillingPlan, BillingPrice } from '@barel/api-client/react';

export const BILLING_PLAN_ORDER: readonly BillingPlan[] = ['discover', 'pro', 'scale', 'agency'];

export function findBillingPrice(
  prices: readonly BillingPrice[],
  plan: BillingPlan,
  cycle: BillingCycle,
): BillingPrice | undefined {
  return prices.find((price) => price.plan === plan && price.cycle === cycle && price.active);
}

export function isPlanUpgrade(current: BillingPlan, target: BillingPlan): boolean {
  return BILLING_PLAN_ORDER.indexOf(target) > BILLING_PLAN_ORDER.indexOf(current);
}

function currencyFractionDigits(currency: string, locale: string): number {
  const options = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).resolvedOptions();
  return options.maximumFractionDigits ?? options.minimumFractionDigits ?? 0;
}

/** Format Stripe minor units without assuming that every currency has two decimals. */
export function formatBillingAmount(unitAmount: number, currency: string, locale: string): string {
  const fractionDigits = currencyFractionDigits(currency, locale);
  const divisor = 10 ** fractionDigits;
  const majorAmount = unitAmount / divisor;
  const hasFraction = Math.abs(unitAmount % divisor) > Number.EPSILON;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: hasFraction ? fractionDigits : 0,
    maximumFractionDigits: fractionDigits,
  }).format(majorAmount);
}

export function formatBillingPrice(price: BillingPrice, locale: string): string {
  return formatBillingAmount(price.unitAmount, price.currency, locale);
}
